import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { TrendingUp, TrendingDown, Users, Calculator, FolderKanban, DollarSign, AlertCircle } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { profitCalculationService } from '../../services/profitCalculationService';
import { userService } from '../../services/userService';
import { projectService } from '../../services/projectService';
import AdminHeader from '../admin/components/AdminHeader';
import MetricCard from './components/MetricCard';
import QuickActionsPanel from './components/QuickActionsPanel';
import RecentCalculationsTable from './components/RecentCalculationsTable';

const AdminDashboard = () => {
  const navigate = useNavigate();
  const { user, profile, signOut, loading: authLoading } = useAuth();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [dashboardData, setDashboardData] = useState({
    totalUsers: 0,
    activeUsers: 0,
    totalProjects: 0,
    totalCalculations: 0,
    recentCalculations: [],
    totalRevenue: 0,
    totalExpenses: 0,
    overallProfitMargin: 0
  });

  useEffect(() => {
    if (!authLoading) {
      if (!user) {
        navigate('/login');
      } else if (profile?.role !== 'admin') {
        navigate('/projeto');
      } else {
        loadDashboardData();
      }
    }
  }, [user, profile, authLoading, navigate]);

  useEffect(() => {
    // Auto-refresh every 30 seconds
    const interval = setInterval(() => {
      loadDashboardData();
    }, 30000);

    return () => clearInterval(interval);
  }, []);

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      setError('');

      // Fetch all data in parallel
      const [users, projects, calculations] = await Promise.all([
        userService?.getAllUsers(),
        projectService?.getAllProjects(),
        profitCalculationService?.getAllCalculations()
      ]);

      // Calculate metrics
      const activeUsers = users?.filter(u => u?.isActive)?.length || 0;
      const recentCalcs = calculations?.slice(0, 5) || [];
      
      const totalRevenue = calculations?.reduce((sum, calc) => sum + (calc?.totalObra || 0), 0);
      const totalExpenses = calculations?.reduce((sum, calc) => sum + (calc?.totalGastos || 0), 0);
      const overallProfit = totalRevenue - totalExpenses;
      const profitMargin = totalRevenue > 0 ? (overallProfit / totalRevenue) * 100 : 0;

      setDashboardData({
        totalUsers: users?.length || 0,
        activeUsers,
        totalProjects: projects?.length || 0,
        totalCalculations: calculations?.length || 0,
        recentCalculations: recentCalcs,
        totalRevenue,
        totalExpenses,
        overallProfitMargin: profitMargin
      });
    } catch (err) {
      setError('Erro ao carregar dados do dashboard: ' + err?.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSignOut = async () => {
    try {
      await signOut();
      navigate('/login');
    } catch (error) {
      console.error('Erro ao sair:', error);
    }
  };

  if (loading || authLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center px-4">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
          <p className="mt-4 text-sm sm:text-base text-muted-foreground">Carregando dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <AdminHeader userName={profile?.fullName || profile?.email} onSignOut={handleSignOut} />
      
      <div className="max-w-7xl mx-auto px-3 sm:px-4 py-4 sm:py-8">
        <div className="mb-6">
          <h1 className="text-2xl sm:text-3xl font-bold text-foreground">Dashboard Administrativo</h1>
          <p className="text-sm sm:text-base text-muted-foreground mt-1 sm:mt-2">
            Visão geral consolidada do sistema CTOBRA
          </p>
        </div>

        {error && (
          <div className="mb-6 p-4 bg-destructive/10 border border-destructive/20 rounded-lg flex items-start gap-3">
            <AlertCircle className="h-5 w-5 text-destructive flex-shrink-0 mt-0.5" />
            <p className="text-sm text-destructive">{error}</p>
          </div>
        )}

        {/* KPI Metrics */}
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4 sm:gap-6 mb-6">
          <MetricCard
            title="Usuários Totais"
            value={dashboardData?.totalUsers}
            subtitle={`${dashboardData?.activeUsers} ativos`}
            icon={<Users className="h-5 w-5" />}
            trend={dashboardData?.activeUsers > 0 ? 'up' : 'neutral'}
          />
          <MetricCard
            title="Projetos"
            value={dashboardData?.totalProjects}
            subtitle="Total cadastrados"
            icon={<FolderKanban className="h-5 w-5" />}
            trend="neutral"
          />
          <MetricCard
            title="Cálculos Realizados"
            value={dashboardData?.totalCalculations}
            subtitle="Histórico completo"
            icon={<Calculator className="h-5 w-5" />}
            trend="neutral"
          />
          <MetricCard
            title="Margem de Lucro"
            value={`${dashboardData?.overallProfitMargin?.toFixed(1)}%`}
            subtitle="Média geral"
            icon={<DollarSign className="h-5 w-5" />}
            trend={dashboardData?.overallProfitMargin > 0 ? 'up' : 'down'}
          />
        </div>

        {/* Quick Actions */}
        <div className="mb-6">
          <QuickActionsPanel />
        </div>

        {/* System Overview Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 sm:gap-6 mb-6">
          <div className="bg-card border border-border rounded-lg p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-foreground">Receita Total</h3>
              <TrendingUp className="h-5 w-5 text-success" />
            </div>
            <p className="text-2xl sm:text-3xl font-bold text-foreground">
              {new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' })?.format(dashboardData?.totalRevenue)}
            </p>
            <p className="text-sm text-muted-foreground mt-2">Soma de todos os cálculos</p>
          </div>

          <div className="bg-card border border-border rounded-lg p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-foreground">Despesas Totais</h3>
              <TrendingDown className="h-5 w-5 text-destructive" />
            </div>
            <p className="text-2xl sm:text-3xl font-bold text-foreground">
              {new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' })?.format(dashboardData?.totalExpenses)}
            </p>
            <p className="text-sm text-muted-foreground mt-2">Total de gastos</p>
          </div>

          <div className="bg-card border border-border rounded-lg p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-foreground">Lucro Líquido</h3>
              {dashboardData?.totalRevenue - dashboardData?.totalExpenses > 0 ? (
                <TrendingUp className="h-5 w-5 text-success" />
              ) : (
                <TrendingDown className="h-5 w-5 text-destructive" />
              )}
            </div>
            <p className={`text-2xl sm:text-3xl font-bold ${
              dashboardData?.totalRevenue - dashboardData?.totalExpenses > 0 ? 'text-success' : 'text-destructive'
            }`}>
              {new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' })?.format(
                dashboardData?.totalRevenue - dashboardData?.totalExpenses
              )}
            </p>
            <p className="text-sm text-muted-foreground mt-2">Resultado consolidado</p>
          </div>
        </div>

        {/* Recent Calculations */}
        <div className="bg-card border border-border rounded-lg p-4 sm:p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg sm:text-xl font-semibold text-foreground">Cálculos Recentes</h3>
            <button
              onClick={() => navigate('/profit-calculation-history')}
              className="text-sm text-primary hover:underline"
            >
              Ver todos
            </button>
          </div>
          <RecentCalculationsTable calculations={dashboardData?.recentCalculations} />
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;